import { useCallback, useRef, useState } from "react";
import { Button } from "../ui/Button";
import { useDismissOnOutside } from "../ui/Overlay";
import { LOCALE_CATALOG, localeOf } from "../../lib/locales";
import { useT } from "../../lib/i18n";

/**
 * 「加一种语言」按钮 + 目录浮层。
 *
 * 从 {@link I18nGrid} 里抽出来给富文本那版共用。抽之前 `RichI18nField` 自己写了一版
 * 语言列表，**只有删、没有加**——运营想给某封邮件补一版日文，得先找一个已经有日文的
 * 对象抄过来，或者干脆发不了。两版各写一遍的必然结果就是其中一版少功能。
 *
 * 浮层用 **fixed** 定位，按按钮实际位置算：用 absolute 会被弹窗 body 的
 * `overflow-auto` 裁掉，而这个按钮恰好总在弹窗底部，展开后只看得到第一行的上半截。
 */
export function AddLocale({ locales, onAdd, disabled }: {
  /** 已经有的语言 */
  locales: string[];
  onAdd: (id: string) => void;
  disabled?: boolean;
}) {
  const t = useT();
  const [pick, setPick] = useState<{ left: number; top: number; up: boolean } | null>(null);
  const box = useRef<HTMLSpanElement>(null);
  // 语言列表是 max-h + overflow-y-auto，不能被自己的滚动关掉
  const panel = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setPick(null), []);
  useDismissOnOutside(pick != null, { box, panel }, close);

  const rest = LOCALE_CATALOG.filter((l) => !locales.includes(l.id));

  const openPick = () => {
    const r = box.current?.getBoundingClientRect();
    if (!r) return;
    const below = window.innerHeight - r.bottom;
    // 横向也要钳：按钮贴近右缘时浮层会伸出视口，右半列语言点不到
    const left = Math.max(8, Math.min(r.left, window.innerWidth - 208));
    setPick({ left, top: below < 280 ? r.top - 6 : r.bottom + 6, up: below < 280 });
  };

  return (
    <span ref={box} className="relative" onClick={(e) => e.stopPropagation()}>
      <Button size="sm" disabled={disabled || !rest.length} onClick={() => (pick ? setPick(null) : openPick())}>
        {rest.length ? t("i18n.addLang") : t("i18n.allAdded")}
      </Button>
      {pick && (
        <div
          ref={panel}
          style={{ left: pick.left, top: pick.top, transform: pick.up ? "translateY(-100%)" : undefined }}
          className="fixed z-[200] max-h-[260px] min-w-[200px] overflow-y-auto rounded-base border border-line-lighter bg-white p-1 shadow-light"
        >
          {rest.map((l) => (
            <div
              key={l.id}
              onClick={() => { setPick(null); onAdd(l.id); }}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2.5 py-1.5 text-sm text-ink-regular hover:bg-fill-light"
            >
              <span className="w-[38px] text-xs text-ink-placeholder">{l.short}</span>
              {localeOf(l.id).label}
            </div>
          ))}
        </div>
      )}
    </span>
  );
}
